import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const QuoteConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const quoteData = location.state?.quoteData;

  useEffect(() => {
    // Render Google Customer Reviews badge après soumission du devis
    if (quoteData && window.gapi) {
      window.renderOptIn = function() {
        window.gapi.load('surveyoptin', function() {
          window.gapi.surveyoptin.render({
            // REQUIRED FIELDS
            "merchant_id": 5755127120,
            "order_id": quoteData.id || `QUOTE-${Date.now()}`,
            "email": quoteData.email,
            "delivery_country": "FR",
            "estimated_delivery_date": calculateEstimatedDate(),
            
            // OPTIONAL FIELDS - Products GTINs
            "products": quoteData.equipmentType ? [{"gtin": getGTIN(quoteData.equipmentType)}] : []
          });
        });
      };
      
      // Déclencher le rendu si l'API est déjà chargée
      if (window.gapi && window.gapi.surveyoptin) {
        window.renderOptIn();
      }
    }
  }, [quoteData]);

  // Calculer la date de livraison estimée (J+30 par défaut)
  const calculateEstimatedDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0]; // Format YYYY-MM-DD
  };

  // Mapper les équipements aux GTINs (vous pourrez les personnaliser)
  const getGTIN = (equipmentType) => {
    const gtinMap = {
      'Niveleurs de quai': '3000000000001',
      'Ponts de transbordement': '3000000000002',
      'Butoirs de quai': '3000000000003',
      'Garde-corps': '3000000000004',
      'Portes sectionnelles': '3000000000005',
      'Autres équipements': '3000000000006'
    };
    return gtinMap[equipmentType] || '3000000000000';
  };

  // Rediriger si pas de données de devis
  useEffect(() => {
    if (!quoteData) {
      const timer = setTimeout(() => navigate('/'), 3000);
      return () => clearTimeout(timer);
    }
  }, [quoteData, navigate]);

  if (!quoteData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600">Redirection vers la page d'accueil...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-16">
        <div className="max-w-2xl mx-auto">
          {/* Success Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
            {/* Success Icon */}
            <div className="mb-6 flex justify-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Demande de devis envoyée !
            </h1>

            {/* Message */}
            <p className="text-lg text-gray-600 mb-8">
              Merci <strong>{quoteData.name}</strong> pour votre demande de devis.
              <br />
              Nous avons bien reçu votre demande et nous vous répondrons dans les <strong>24 heures</strong>.
            </p>

            {/* Quote Details Summary */}
            <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left">
              <h3 className="font-semibold text-slate-900 mb-4">Récapitulatif de votre demande :</h3>
              <div className="space-y-2 text-sm text-gray-700">
                {quoteData.company && (
                  <p><strong>Entreprise :</strong> {quoteData.company}</p>
                )}
                <p><strong>Email :</strong> {quoteData.email}</p>
                {quoteData.phone && (
                  <p><strong>Téléphone :</strong> {quoteData.phone}</p>
                )}
                {quoteData.equipmentType && (
                  <p><strong>Type d'équipement :</strong> {quoteData.equipmentType}</p>
                )}
                {quoteData.location && (
                  <p><strong>Localisation :</strong> {quoteData.location}</p>
                )}
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 mb-8 text-left">
              <h3 className="font-semibold text-orange-900 mb-3">Prochaines étapes :</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-orange-800">
                <li>Étude personnalisée de votre projet</li>
                <li>Devis détaillé sous 24h par email</li>
                <li>Échange téléphonique si nécessaire</li>
                <li>Installation sur mesure conforme Carsat</li>
              </ol>
            </div>

            {/* Contact Info */}
            <div className="mb-8 text-gray-600 text-sm">
              <p>Une question urgente ? Contactez-nous :</p>
              <p className="font-semibold text-slate-900 mt-2">
                📞 <a href="tel:+33659452562" className="hover:text-orange-600">06 59 45 25 62</a>
              </p>
              <p className="font-semibold text-slate-900">
                ✉️ <a href="mailto:gestion@orion-industrie.eu" className="hover:text-orange-600">
                  gestion@orion-industrie.eu
                </a>
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/')}
                className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 rounded-lg text-base font-semibold"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à l'accueil
              </Button>
            </div>
          </div>

          {/* Google Customer Reviews Info */}
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>Nous apprécierions votre avis une fois votre projet terminé 🙏</p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default QuoteConfirmation;
